import { useState, useEffect, useRef } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import { useUser } from "@clerk/react";
import { watchProvider, unwatchProvider, queueConfirmation } from "../utils/alertsApi";
import { getProviderFlags } from "../utils/cmsApi";

export default function WatchProviderButton({ npi, providerName, initialWatched = false, onWatchChange }) {  
  const { isPro, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const { user, isSignedIn } = useUser();
  const [isWatched, setIsWatched] = useState(initialWatched);

  // Sync with parent when initialWatched changes asynchronously
  useEffect(() => {
    setIsWatched(initialWatched);
  }, [initialWatched]);  
  const [watchLoading, setWatchLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [alertPayments, setAlertPayments] = useState(true);
  const [alertExclusions, setAlertExclusions] = useState(true);
  const [paymentThreshold, setPaymentThreshold] = useState("");
  const [yearlyThreshold, setYearlyThreshold] = useState("");
  const [popoverPosition, setPopoverPosition] = useState("bottom");
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!showPopover) return;
    function handleClickOutside(e) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setShowPopover(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showPopover]);

  // Determine if popover should open above or below
  function handleWatchClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch providers and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching providers and receiving email alerts is a Pro feature. Upgrade to get notified when payment data changes.");
      setShowUpgradeModal(true);
      return;
    }
    if (isWatched) {
      setWatchLoading(true);
      unwatchProvider(user.id, npi)
        .then(() => {
          setIsWatched(false);
          if (onWatchChange) onWatchChange(npi, false);
        })
        .catch((err) => console.error("Unwatch error:", err))
        .finally(() => setWatchLoading(false));
    } else {
      // Check if button is near the top of the viewport
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const popoverHeight = 380; // approximate popover height
        if (rect.top < popoverHeight) {
          setPopoverPosition("bottom");
        } else {
          setPopoverPosition("top");
        }
      }
      setShowPopover(true);
    }
  }

  async function handleStartWatching(e) {
    e.preventDefault();
    e.stopPropagation();
    setWatchLoading(true);
    setShowPopover(false);
    try {
      const flags = await getProviderFlags(npi);
      await watchProvider(user.id, npi, providerName, {
        alert_payments: alertPayments,
        alert_exclusions: alertExclusions,
        payment_threshold: paymentThreshold ? parseFloat(paymentThreshold) : null,
        yearly_threshold: yearlyThreshold ? parseFloat(yearlyThreshold) : null,
        has_payments: flags.hasPayments,
        has_ownership: flags.hasOwnership,
        has_research: flags.hasResearch,
        is_excluded: flags.isExcluded,
      });      
      // Queue confirmation email
      await queueConfirmation(user.id, "provider", npi, providerName, {
        alert_payments: alertPayments,
        alert_exclusions: alertExclusions,
        payment_threshold: paymentThreshold ? parseFloat(paymentThreshold) : null,
        yearly_threshold: yearlyThreshold ? parseFloat(yearlyThreshold) : null,
      });
      setIsWatched(true);
      if (onWatchChange) onWatchChange(npi, true);      
    } catch (err) {
      console.error("Watch error:", err);
    } finally {
      setWatchLoading(false);
    }
  }

  return (
    <div className="relative" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <button
        ref={buttonRef}
        onClick={handleWatchClick}
        disabled={watchLoading}
        className={`text-xs px-3 py-1 rounded-lg border transition-all ${
          isWatched
            ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
            : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
        }`}
      >
        {watchLoading ? "..." : isWatched ? "🔔 Watching" : "🔕 Watch"}
      </button>

      {showPopover && (
        <div
          ref={popoverRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={`absolute ${popoverPosition === "top" ? "bottom-full mb-2" : "top-full mt-2"} right-0 w-72 bg-navy-800 border border-white/15 rounded-xl p-4 z-50 shadow-2xl`}
        >            
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/80 text-sm font-medium">Configure alerts</p>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPopover(false); }}
              className="text-white/30 hover:text-white/70 text-xs"
            >
              ✕
            </button>
          </div>

          {/* Payment alerts toggle */}
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <div>
              <p className="text-white/70 text-xs font-medium">Payment alerts</p>
              <p className="text-white/30 text-xs mt-0.5">Notify when new payments are published</p>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAlertPayments(!alertPayments); }}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                alertPayments ? "bg-gold-500" : "bg-white/10"
              }`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                alertPayments ? "translate-x-4" : "translate-x-0.5"
              }`} />
            </button>
          </div>

          {/* Exclusion alerts toggle */}
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <div>
              <p className="text-white/70 text-xs font-medium">Exclusion alerts</p>
              <p className="text-white/30 text-xs mt-0.5">Notify if added to OIG or SAM list</p>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAlertExclusions(!alertExclusions); }}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                alertExclusions ? "bg-gold-500" : "bg-white/10"
              }`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                alertExclusions ? "translate-x-4" : "translate-x-0.5"
              }`} />
            </button>
          </div>

          {/* Single payment threshold */}
          <div className="py-2 border-b border-white/5">
            <p className="text-white/70 text-xs font-medium mb-1">Single payment threshold</p>
            <p className="text-white/30 text-xs mb-2">Alert when a single payment exceeds this amount</p>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs">$</span>
              <input
                type="number"
                placeholder="e.g. 5000 (optional)"
                value={paymentThreshold}
                onChange={(e) => { e.stopPropagation(); setPaymentThreshold(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="flex-1 bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/60"
              />              
            </div>
          </div>

          {/* Yearly threshold */}
          <div className="py-2 border-b border-white/5">
            <p className="text-white/70 text-xs font-medium mb-1">Annual total threshold</p>
            <p className="text-white/30 text-xs mb-2">Alert when total yearly payments exceed this amount</p>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-xs">$</span>
              <input
                type="number"
                placeholder="e.g. 50000 (optional)"
                value={yearlyThreshold}
                onChange={(e) => { e.stopPropagation(); setYearlyThreshold(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="flex-1 bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/60"
              />              
            </div>
          </div>

          <button
            onClick={handleStartWatching}
            className="btn-primary w-full text-xs py-2 mt-3"
          >
            🔔 Start Watching
          </button>
        </div>
      )}
    </div>
  );
}